import {
  Avatar,
  Box,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import type { AccountProfileAdministratorDto, AccountProfileDrawerResponse } from "@lib/ShiOneClient";

function initials(name?: string | null) {
  return name
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() ?? "";
}

function SectionHeader({ title, subtitle }: Readonly<{ title: string; subtitle?: string }>) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body1" fontWeight={500}>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      )}
    </Box>
  );
}

function MetadataItem({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Box minWidth={0}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary" title={value} sx={{ mt: 0.25, fontWeight: 500, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Box>
  );
}

function AdminRow({ admin }: Readonly<{ admin: AccountProfileAdministratorDto }>) {
  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.5,
        gap: 2,
        bgcolor: "background.paper",
        width: "100%",
        borderRadius: 1,
      }}
    >
      <Avatar
        src={admin.photoUrl ?? undefined}
        alt={admin.fullName ?? "Account administrator"}
        sx={{ width: 38, height: 38, bgcolor: "action.selected", fontSize: 15, fontWeight: 600, color: "text.secondary" }}
      >
        {initials(admin.fullName)}
      </Avatar>
      <Box minWidth={0} flex={1}>
        <Typography variant="body1" noWrap title={admin.fullName ?? undefined} sx={{ fontWeight: 500 }}>
          {admin.fullName}
        </Typography>
        {admin.email && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <EmailIcon fontSize="small" color="action" />
            <Link href={`mailto:${admin.email}`} variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {admin.email.toLowerCase()}
            </Link>
          </Box>
        )}
        {admin.phoneNumber && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {admin.phoneNumber}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default function CompanyProfileTab({
  account,
  companyLogoUrl,
}: Readonly<{ account: AccountProfileDrawerResponse; companyLogoUrl?: string }>) {
  const displayLogoUrl = companyLogoUrl ?? account.companyLogoUrl;

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      {/* Logo + company info row — no card border/padding */}
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar
          src={displayLogoUrl ?? undefined}
          alt={displayLogoUrl ? account.accountName ?? "Company logo" : undefined}
          sx={{
            width: 88,
            height: 88,
            fontSize: 24,
            fontWeight: 700,
            flexShrink: 0,
          }}
          imgProps={{ style: { objectFit: "contain" } }}
        >
          {initials(account.accountName)}
        </Avatar>
        <Box minWidth={0} flex={1}>
          <Typography variant="h5" title={account.accountName} sx={{ fontWeight: 700, lineHeight: 1.2, overflowWrap: "anywhere" }}>
            {account.accountName}
          </Typography>
          {account.storefrontName && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, mt: 2 }}>
              <MetadataItem label="Storefront" value={account.storefrontName} />
            </Box>
          )}
        </Box>
      </Stack>

      {/* Account Administrators */}
      <Box>
        <SectionHeader
          title="Account Administrators"
          subtitle="People in your organization who can help manage account access"
        />
        {(account.administrators ?? []).length ? (
          <Stack spacing={1.5}>
            {(account.administrators ?? []).map((admin) => (
              <AdminRow key={admin.email || admin.fullName} admin={admin} />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No account administrators found.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
